export interface WordDiffToken {
  type: "same" | "added" | "removed";
  value: string;
}

export function computeWordDiff(
  oldText: string,
  newText: string
): {
  oldTokens: WordDiffToken[];
  newTokens: WordDiffToken[];
} {
  const tokenize = (s: string) => s.match(/\S+|\s+/g) || [];
  const wordsA = tokenize(oldText || "");
  const wordsB = tokenize(newText || "");

  if (wordsA.length === 0) {
    return {
      oldTokens: [],
      newTokens: wordsB.map((w) => ({ type: "added", value: w })),
    };
  }
  if (wordsB.length === 0) {
    return {
      oldTokens: wordsA.map((w) => ({ type: "removed", value: w })),
      newTokens: [],
    };
  }

  // Classic LCS on word/whitespace tokens
  const n = Math.min(wordsA.length, 600);
  const m = Math.min(wordsB.length, 600);
  const dp: number[][] = Array.from({ length: n + 1 }, () =>
    new Array(m + 1).fill(0)
  );

  for (let i = 0; i < n; i++) {
    for (let j = 0; j < m; j++) {
      if (wordsA[i] === wordsB[j]) {
        dp[i + 1][j + 1] = dp[i][j] + 1;
      } else {
        dp[i + 1][j + 1] = Math.max(dp[i + 1][j], dp[i][j + 1]);
      }
    }
  }

  let i = n;
  let j = m;
  const aTokens: WordDiffToken[] = [];
  const bTokens: WordDiffToken[] = [];

  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && wordsA[i - 1] === wordsB[j - 1]) {
      aTokens.unshift({ type: "same", value: wordsA[i - 1] });
      bTokens.unshift({ type: "same", value: wordsB[j - 1] });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      bTokens.unshift({ type: "added", value: wordsB[j - 1] });
      j--;
    } else if (i > 0 && (j === 0 || dp[i][j - 1] < dp[i - 1][j])) {
      aTokens.unshift({ type: "removed", value: wordsA[i - 1] });
      i--;
    }
  }

  return { oldTokens: aTokens, newTokens: bTokens };
}
